import React from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import * as S from '../../styles';
import { BUTTONS, DIALOGS } from '../../TransportCloudTable.consts';

interface ConfirmMultiEditDialogProps {
  open: boolean;
  position: { top: number; left?: number; right?: number };
  itemId: string;
  columnKey: string;
  confirmationText?: string;
  anchorSelector?: string;
  align?: 'left' | 'right';
  onConfirm: (persistChecked: boolean) => void;
  onCancel: () => void;
  onClickOutside: () => void;
}

export const ConfirmMultiEditDialog: React.FC<ConfirmMultiEditDialogProps> = ({
  open,
  position,
  itemId,
  columnKey,
  confirmationText,
  anchorSelector,
  align = 'left',
  onConfirm,
  onCancel,
  onClickOutside,
}) => {
  const [currentPosition, setCurrentPosition] = React.useState(position);
  const [isVisible, setIsVisible] = React.useState(false);
  const [uncheckAfterSaving, setUncheckAfterSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setUncheckAfterSaving(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setIsVisible(false);
      return;
    }
    setIsVisible(true);

    const updatePosition = () => {
      const cellElement = document.querySelector(
        anchorSelector ??
          `[data-item-id="${itemId}"][data-column="${columnKey}"]`,
      ) as HTMLElement;

      if (cellElement) {
        const rect = cellElement.getBoundingClientRect();
        setCurrentPosition(
          align === 'right'
            ? {
                top: rect.bottom + window.scrollY + 5,
                right: window.innerWidth - rect.right - window.scrollX,
              }
            : {
                top: rect.bottom + window.scrollY + 5,
                left: rect.left + window.scrollX,
              },
        );
      }
    };

    const scrollContainer = document.querySelector('.MuiTableContainer-root');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', updatePosition);
    }
    window.addEventListener('scroll', updatePosition);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', updatePosition);
      }
      window.removeEventListener('scroll', updatePosition);
    };
  }, [open, itemId, columnKey, anchorSelector, align]);

  React.useEffect(() => {
    setCurrentPosition(position);
  }, [position]);

  if (!open) return null;

  return (
    <>
      <S.ModalBackdrop onClick={onClickOutside} />
      <S.ModalPaper
        sx={{
          top: `${currentPosition.top}px`,
          ...(currentPosition.left !== undefined
            ? { left: `${currentPosition.left}px` }
            : { right: `${currentPosition.right}px` }),
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.1s ease-in',
        }}
      >
        <S.ModalContent>
          {confirmationText ?? DIALOGS.multiEditingConfirmation}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={uncheckAfterSaving}
                  onChange={(e) => setUncheckAfterSaving(e.target.checked)}
                />
              }
              label={DIALOGS.multiEditingCheck}
              sx={{
                margin: '12px 0 0 0',
                padding: '0 0 0 0',
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.75rem',
                  marginLeft: '0px',
                },
              }}
            />
          </Box>
        </S.ModalContent>
        <S.ModalActions>
          <S.TCStyledButton
            $primary
            onClick={() => onConfirm(!uncheckAfterSaving)}
          >
            {BUTTONS.yes}
          </S.TCStyledButton>
          <S.TCStyledButton onClick={onCancel}>{BUTTONS.no}</S.TCStyledButton>
        </S.ModalActions>
      </S.ModalPaper>
    </>
  );
};
